import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '../../Icon';
import { SnackbarItem } from '../SnackbarItem';

import styles from './Snackbar.module.scss';

type Props = SnackbarItem & {
  onClose: (id: string) => void;
};

const typeToConfigMapping = {
  success: {
    icon: 'successNew',
    className: styles.success,
    iconClassName: styles.iconSuccess,
  },
  error: {
    icon: 'errorNew',
    className: styles.error,
    iconClassName: styles.iconError,
  },
  warning: {
    icon: 'warningNew',
    className: styles.warning,
    iconClassName: styles.iconWarning,
  },
};

export function Snackbar({
  onClose,
  id,
  lifeSpan = 5000,
  iconType = 'warning',
  link,
  text,
  linkText,
}: Props): JSX.Element {
  const rootElem = useRef<HTMLDivElement>(null);
  const timelineElem = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const timeStart = useRef(new Date().getTime());
  const timeRest = useRef(lifeSpan);

  const handleClose = (): void => {
    if (rootElem.current !== null) {
      rootElem.current.style.height = `${rootElem.current.offsetHeight}px`;
    }
    setIsClosing(true);
  };

  const config = typeToConfigMapping[iconType];

  useEffect(() => {
    const element = rootElem.current;
    let closeTimeout = setTimeout(() => setIsClosing(true), timeRest.current);
    let timelineTimeout: number;
    const startTimeLine = (): void => {
      if (timelineElem.current) {
        timelineElem.current.style.transition = `width ${timeRest.current}ms linear`;
      }

      timelineTimeout = window.setTimeout(() => {
        if (timelineElem.current) {
          timeStart.current = new Date().getTime();
          timelineElem.current.style.width = '0';
        }
      }, 50);
    };

    startTimeLine();

    const snackbarClosing = (): void => {
      startTimeLine();

      closeTimeout = setTimeout(() => setIsClosing(true), timeRest.current);
    };

    const resetClosing = (): void => {
      if (timelineElem.current) {
        timeRest.current -= new Date().getTime() - timeStart.current;
        timelineElem.current.style.width = `${timelineElem.current.offsetWidth}px`;
      }
      clearTimeout(closeTimeout);
      clearTimeout(timelineTimeout);
    };

    if (element !== null) {
      element.style.height = `${element.offsetHeight + 20}px`;
      element.addEventListener('mouseenter', resetClosing);
      element.addEventListener('mouseleave', snackbarClosing);
    }

    return () => {
      resetClosing();

      if (element !== null) {
        element.removeEventListener('mouseenter', resetClosing);
        element.removeEventListener('mouseleave', snackbarClosing);
      }
    };
  }, []);

  useEffect(() => {
    const element = rootElem.current;

    const onAnimationEnd = (): void => {
      if (element !== null && !isClosing) {
        element.style.height = '';
      }
    };

    if (element !== null) {
      if (isClosing) {
        element.style.height = '0';

        setTimeout(() => {
          if (id !== undefined) onClose(id);
        }, 500);
      } else {
        element.addEventListener('animationend', onAnimationEnd);
      }
    }

    return () => {
      if (element !== null) {
        element.removeEventListener('animationend', onAnimationEnd);
      }
    };
  }, [id, isClosing, onClose]);

  return (
    <div
      ref={rootElem}
      data-component="Snackbar"
      className={cn(
        styles.root,
        config.className,
        isClosing ? styles.fadeOut : styles.fadeIn,
      )}
    >
      <div>
        <div className={styles.item}>
          <Icon icon={config.icon} className={config.iconClassName} />

          <div className={styles.inner}>
            <div>{text}</div>

            {link !== undefined && linkText !== undefined && (
              <p>
                <a href={link} target="_blank" rel="noreferrer">
                  {linkText}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>

      <div onClick={handleClose} className={styles.closeBtn}>
        <Icon icon="close" className={styles.closeIcon} />
      </div>

      <div className={styles.timeline} ref={timelineElem} />
    </div>
  );
}
